import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RefreshCw, CheckCircle2, Clock, Ban } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SYNC_STATUS = {
  enviado: { label: 'Sincronizado', cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300', Icon: CheckCircle2 },
  pendiente: { label: 'Pendiente', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300', Icon: Clock },
  anulacion_pendiente: { label: 'Anulación pendiente', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300', Icon: Clock },
  anulado: { label: 'Anulado', cls: 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300', Icon: Ban },
  cancelado: { label: 'Cancelado', cls: 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300', Icon: Ban },
};

const PARTE_LABEL = { ADL: 'Con adelanto (tienda)', GER: 'Pagado por Gerencia' };

export const SyncPill = ({ status }) => {
  const s = SYNC_STATUS[status] || SYNC_STATUS.pendiente;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${s.cls}`}>
      <s.Icon className="h-3 w-3" /> {s.label}
    </span>
  );
};

export const useRetrySync = (onDone) => useCallback(async (recId) => {
  try {
    const r = await axios.post(`${API}/flujo-sync/${recId}/retry`);
    if (r.data?.status === 'enviado' || r.data?.status === 'anulado') toast.success('Sincronizado con Flujo de Caja');
    else toast.error(r.data?.error || 'Sigue pendiente');
    onDone && onDone();
  } catch (e) {
    toast.error(e.response?.data?.detail || 'No se pudo reintentar');
  }
}, [onDone]);

export const FlujoSyncBadge = ({ origenId, resyncable = false }) => {
  const { user } = useAuth();
  const allowed = user?.role === 'admin' || (user?.permissions || []).includes('gestionar_sync_flujo');
  const [recs, setRecs] = useState([]);
  const load = useCallback(() => {
    axios.get(`${API}/flujo-sync`, { params: { origen_id: origenId } }).then(r => setRecs(r.data || [])).catch(() => {});
  }, [origenId]);
  const retry = useRetrySync(load);

  useEffect(() => { if (allowed && origenId) load(); }, [allowed, origenId, load]);
  const resync = async () => {
    try {
      const r = await axios.post(`${API}/flujo-sync/payments/${origenId}/resync`);
      setRecs(r.data || []);
      toast[(r.data || []).length ? 'success' : 'error']((r.data || []).length ? 'Enviado al Flujo de Caja' : 'Este pago no corresponde a una tienda enlazada');
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo enviar'); }
  };
  if (!allowed || (recs.length === 0 && !resyncable)) return null;

  return (
    <Card className="p-4 border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900" data-testid="flujo-sync-card">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-3 flex items-center gap-2">
        <RefreshCw className="h-3.5 w-3.5" /> Flujo de Caja
        {resyncable && (
          <Button size="sm" variant="ghost" className="ml-auto h-7 normal-case tracking-normal" onClick={resync} data-testid="flujo-resync-btn">
            Reenviar
          </Button>
        )}
      </h3>
      {recs.length === 0 && <p className="text-sm text-slate-500 dark:text-slate-400">Aún no se ha enviado al Flujo de Caja.</p>}
      <ul className="space-y-2">
        {recs.map((r) => (
          <li key={r.id} className="flex items-center justify-between gap-2 text-sm flex-wrap" data-testid={`flujo-sync-${r.id}`}>
            <div className="min-w-0">
              <span className="font-medium text-slate-800 dark:text-slate-200">
                {r.tipo === 'ingreso' ? 'Ingreso · Adelanto' : `Gasto · ${PARTE_LABEL[r.parte] || r.parte}`}
              </span>
              <span className="text-slate-500 dark:text-slate-400 tabular-nums"> · Q{Number(r.monto).toFixed(2)}</span>
              {r.error && r.status !== 'enviado' && <p className="text-[11px] text-red-600 dark:text-red-400 break-words">{r.error}</p>}
            </div>
            <div className="flex items-center gap-2">
              <SyncPill status={r.status} />
              {(r.status === 'pendiente' || r.status === 'anulacion_pendiente') && (
                <Button size="sm" variant="outline" className="h-7" onClick={() => retry(r.id)} data-testid={`flujo-retry-${r.id}`}>
                  Reintentar
                </Button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
};
